import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Modal } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";

import Pagination from "../components/Pagination";
import ViewSalesInvoice from "./ViewSalesInvoice";
import { generateSalesLedgerPdfBlob } from "../utils/generateSalesLedgerPdf";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { parseApiPagination } from "../utils/parseApiPagination";
import { resolveSaleIsQuote } from "../utils/salesErpAdapter";
import {
  postSalesLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";

const ENTIRE_REPORT_DEFAULT_LIMIT = 10;

function numOrZero(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Backend sends old_gold / old_silver as { gram, amount }. */
function pickGmAmount(value) {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return {
      gram: numOrZero(value.gram),
      amount: numOrZero(value.amount),
    };
  }
  return { gram: 0, amount: 0 };
}

function processLedgerEntries(entries) {
  return entries.map((entry) => {
    let formattedDate = entry.invoice_date || "-";
    if (formattedDate !== "-" && String(formattedDate).includes("-")) {
      const dateParts = String(formattedDate).split(" ")[0].split("-");
      if (dateParts.length === 3) {
        formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      }
    }

    const oldGold = pickGmAmount(entry.old_gold);
    const oldSilver = pickGmAmount(entry.old_silver);

    return {
      id: entry.sale_id,
      sale_id: entry.sale_id,
      entry,
      name: entry.name || "-",
      invoice_no: entry.invoice_no || "-",
      invoice_date: formattedDate,
      invoice_value: numOrZero(entry.grand_total),
      taxable_value: numOrZero(entry.final_amount),
      sgst: numOrZero(entry.sgst),
      cgst: numOrZero(entry.cgst),
      igst: numOrZero(entry.igst),
      total_tax: numOrZero(entry.gst_amount),
      gold_gm: numOrZero(entry.gold_gm),
      silver_gm: numOrZero(entry.silver_gm),
      cash: numOrZero(entry.cash),
      upi: numOrZero(entry.upi),
      bank: numOrZero(entry.bank),
      card: numOrZero(entry.card),
      old_gold_gm: oldGold.gram,
      old_gold_amt: oldGold.amount,
      old_silver_gm: oldSilver.gram,
      old_silver_amt: oldSilver.amount,
    };
  });
}

function summarizeLedgerRows(rows) {
  return rows.reduce(
    (acc, entry) => {
      acc.invoice_value += entry.invoice_value;
      acc.taxable_value += entry.taxable_value;
      acc.sgst += entry.sgst;
      acc.cgst += entry.cgst;
      acc.igst += entry.igst;
      acc.total_tax += entry.total_tax;
      acc.gold_gm += entry.gold_gm;
      acc.silver_gm += entry.silver_gm;
      acc.cash += entry.cash || 0;
      acc.upi += entry.upi || 0;
      acc.bank += entry.bank || 0;
      acc.card += entry.card || 0;
      acc.old_gold_gm += entry.old_gold_gm || 0;
      acc.old_gold_amt += entry.old_gold_amt || 0;
      acc.old_silver_gm += entry.old_silver_gm || 0;
      acc.old_silver_amt += entry.old_silver_amt || 0;
      return acc;
    },
    {
      invoice_value: 0,
      taxable_value: 0,
      sgst: 0,
      cgst: 0,
      igst: 0,
      total_tax: 0,
      gold_gm: 0,
      silver_gm: 0,
      cash: 0,
      upi: 0,
      bank: 0,
      card: 0,
      old_gold_gm: 0,
      old_gold_amt: 0,
      old_silver_gm: 0,
      old_silver_amt: 0,
    },
  );
}

function SalesLedger({
  masterGoldRate,
  masterSilverRate,
  showLiveRate = false,
}) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ENTIRE_REPORT_DEFAULT_LIMIT);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfUrlRef = useRef(null);
  const [showEditSaleModal, setShowEditSaleModal] = useState(false);
  const [editSaleId, setEditSaleId] = useState(null);
  const [editSaleInvoices, setEditSaleInvoices] = useState([]);
  const [editSaleIsQuote, setEditSaleIsQuote] = useState(undefined);
  const pageCacheRef = useRef(new Map());

  const fetchSalesLedger = useCallback(
    async ({
      page = 1,
      limit = ENTIRE_REPORT_DEFAULT_LIMIT,
      silent = false,
      bypassCache = false,
    } = {}) => {
      const cacheKey = entireReport ? `${page}:${limit}` : "";
      if (cacheKey && !bypassCache) {
        const cached = pageCacheRef.current.get(cacheKey);
        if (cached) {
          setLedgerRows(cached.ledgerRows);
          setSummaryData(cached.summaryData);
          setCurrentPage(cached.currentPage);
          setTotalCount(cached.totalCount);
          setHasMorePages(cached.hasMorePages);
          setLoading(false);
          return;
        }
      }

      setLoading(true);

      const apiPayload = {};

      if (entireReport) {
        apiPayload.type = "entire";
        apiPayload.page = page;
        apiPayload.limit = limit;
      } else {
        if (fromDate) apiPayload.from_date = fromDate;
        if (toDate) apiPayload.to_date = toDate;
      }

      try {
        const payload = await postSalesLedger(apiPayload);

        if (
          !payload ||
          (!isErpApiSuccess(payload) && payload.message !== "radha")
        ) {
          toast.error(
            payload?.message || "Failed to fetch ledger data. Please try again.",
          );
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          setHasMorePages(false);
          return;
        }

        const entries = payload.data?.entries || payload.data || [];

        if (!Array.isArray(entries)) {
          toast.error("Invalid ledger response from server.");
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          setHasMorePages(false);
          return;
        }

        if (entries.length === 0) {
          if (!silent) {
            toast.error("No ledger entries found for the selected filters.");
          }
          setLedgerRows([]);
          setSummaryData(null);
          if (entireReport) {
            const pagination = parseApiPagination(payload, 0);
            setCurrentPage(pagination.page);
            setTotalCount(pagination.total);
            setHasMorePages(pagination.hasMore);
          } else {
            setTotalCount(0);
            setHasMorePages(false);
            setCurrentPage(1);
          }
          return;
        }

        const processedEntries = processLedgerEntries(entries);
        const summary = summarizeLedgerRows(processedEntries);

        setLedgerRows(processedEntries);
        setSummaryData(summary);

        const paginationMeta = entireReport
          ? parseApiPagination(payload, processedEntries.length)
          : null;

        if (entireReport && paginationMeta) {
          setCurrentPage(paginationMeta.page);
          setTotalCount(paginationMeta.total);
          setHasMorePages(paginationMeta.hasMore);
          if (paginationMeta.limit) {
            setItemsPerPage(paginationMeta.limit);
          }
        } else {
          setTotalCount(processedEntries.length);
          setHasMorePages(false);
          setCurrentPage(1);
        }

        if (cacheKey) {
          pageCacheRef.current.set(cacheKey, {
            ledgerRows: processedEntries,
            summaryData: summary,
            currentPage: paginationMeta?.page || page,
            totalCount: paginationMeta?.total ?? processedEntries.length,
            hasMorePages: Boolean(paginationMeta?.hasMore),
          });
        }

        if (!silent) {
          toast.success("Ledger generated successfully!");
        }
      } catch (error) {
        toast.error(
          getErpRequestErrorMessage(
            error,
            "An error occurred while fetching the ledger data.",
          ),
        );
        console.error("Error fetching sales ledger:", error);
        setLedgerRows([]);
        setSummaryData(null);
        setTotalCount(0);
        setHasMorePages(false);
      } finally {
        setLoading(false);
      }
    },
    [entireReport, fromDate, toDate],
  );

  const fetchRef = useRef(fetchSalesLedger);
  fetchRef.current = fetchSalesLedger;
  const ignoreNextPageFetchRef = useRef(false);

  const initialFetchRef = useRef(false);
  // Fetch data on component mount
  useEffect(() => {
    const didRunRef = initialFetchRef.current;
    if (didRunRef) return;
    initialFetchRef.current = true;
    fetchRef.current();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in SalesLedger, refetching ledger...",
        event.detail,
      );
      pageCacheRef.current.clear();
      fetchRef.current({
        page: currentPage,
        limit: itemsPerPage,
        silent: true,
        bypassCache: true,
      });
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    pageCacheRef.current.clear();
    setCurrentPage(1);
    await fetchSalesLedger({
      page: 1,
      limit: itemsPerPage,
      bypassCache: true,
    });
  };

  const handlePageChange = (page) => {
    if (ignoreNextPageFetchRef.current) {
      ignoreNextPageFetchRef.current = false;
      return;
    }
    setCurrentPage(page);
    fetchSalesLedger({ page, limit: itemsPerPage, silent: true });
  };

  const handleItemsPerPageChange = (limit) => {
    ignoreNextPageFetchRef.current = true;
    pageCacheRef.current.clear();
    setItemsPerPage(limit);
    setCurrentPage(1);
    fetchSalesLedger({ page: 1, limit, silent: true, bypassCache: true });
  };

  const handlePrint = async () => {
    if (!ledgerRows.length && totalCount === 0) {
      toast.error("Please generate the ledger first.");
      return;
    }
    if (printLoading) return;

    setPrintLoading(true);

    const printPayload = {
      page: 0,
      limit: 0,
    };

    if (entireReport) {
      printPayload.type = "entire";
      printPayload.filter_type = "entire";
    } else {
      if (fromDate) printPayload.from_date = fromDate;
      if (toDate) printPayload.to_date = toDate;
    }

    try {
      const payload = await postSalesLedger(printPayload);

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        toast.error(
          payload?.message || "Failed to fetch ledger data for printing. Please try again.",
        );
        return;
      }

      const entries = payload.data?.entries || payload.data || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found to print.");
        return;
      }

      const processedEntries = processLedgerEntries(entries);
      const summary = summarizeLedgerRows(processedEntries);

      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }

      const blob = await generateSalesLedgerPdfBlob({
        ledgerData: processedEntries,
        summaryData: summary,
      });

      const blobUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = blobUrl;
      setPdfBlobUrl(blobUrl);
      setShowPDFModal(true);
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while generating the print data.",
        ),
      );
      console.error("Error generating sales ledger for print:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Format currency with ₹ symbol and Indian commas
  const formatCurrency = (value) => {
    if (!value || value === 0) return "-";
    return `₹ ${Math.round(value).toLocaleString("en-IN")}`;
  };

  // Format currency with decimals in Indian format
  const formatCurrencyDecimal = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return "-";
    return `₹ ${n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format weight with 3 decimal places
  const formatWeight = (value) => {
    if (!value || value === 0) return "-";
    return value.toFixed(3);
  };

  // Amount first, gm in brackets — e.g. "₹ 1,200.00 (12.500)" in Indian format
  const formatOldMetalPayment = (amount, gm) => {
    const amt = Number(amount) || 0;
    const grams = Number(gm) || 0;
    if (amt === 0 && grams === 0) return "-";
    const amtText = `₹ ${amt.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
    return `${amtText} (${grams.toFixed(3)})`;
  };

  const closeEditSaleModal = useCallback(
    (opts = {}) => {
      setShowEditSaleModal(false);
      setEditSaleId(null);
      setEditSaleInvoices([]);
      setEditSaleIsQuote(undefined);
      if (opts?.saved) {
        pageCacheRef.current.clear();
        void fetchSalesLedger({
          page: currentPage,
          limit: entireReport ? itemsPerPage : ENTIRE_REPORT_DEFAULT_LIMIT,
          bypassCache: true,
          silent: true,
        });
      }
    },
    [currentPage, entireReport, itemsPerPage, fetchSalesLedger],
  );

  const handleInvoiceClick = (row) => {
    const saleId = row.sale_id ?? row.entry?.sale_id;
    const voucherNumber = row.invoice_no !== "-" ? row.invoice_no : "";
    const isQuote = resolveSaleIsQuote({
      voucherNumber,
      estimate: row.entry?.estimate,
      is_quote: row.entry?.is_quote,
    });

    if (!saleId) {
      toast.error("Invoice details not available.");
      return;
    }

    setEditSaleId(saleId);
    setEditSaleIsQuote(isQuote);
    setEditSaleInvoices([
      {
        id: saleId,
        voucherNumber,
        estimate: row.entry?.estimate,
        isQuote,
      },
    ]);
    setShowEditSaleModal(true);
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className={`card ${cardClass}`}>
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Sales Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* From Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Sales
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                    disabled={printLoading || loading}
                  >
                    {printLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Fetching records...
                      </>
                    ) : (
                      "Print Report"
                    )}
                  </button>
                </div>
                <div className={`ledger-report-wrap mb-4 ${cardClass}`}>
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle ledger-report-table">
                      <thead>
                        <tr>
                          <th rowSpan="2" className="text-center text-nowrap">
                            NAME
                          </th>
                          <th colSpan="3" className="text-center">
                            INVOICE DETAILS
                          </th>
                          <th rowSpan="2" className="text-end text-nowrap">
                            TAXABLE VALUE
                          </th>
                          <th colSpan="4" className="text-center">
                            AMOUNT OF TAX
                          </th>
                          <th colSpan="2" className="text-center">
                            WEIGHT (GM)
                          </th>
                          <th colSpan="6" className="text-center">
                            PAYMENT
                          </th>
                        </tr>
                        <tr>
                          <th className="text-nowrap">INVOICE NO</th>
                          <th className="text-center text-nowrap">DATE</th>
                          <th className="text-end text-nowrap">TOTAL AMT</th>
                          <th className="text-center text-nowrap">SGST</th>
                          <th className="text-center text-nowrap">CGST</th>
                          <th className="text-center text-nowrap">IGST</th>
                          <th className="text-center text-nowrap">TOTAL</th>
                          <th className="text-center text-nowrap">GOLD</th>
                          <th className="text-center text-nowrap">SILVER</th>
                          <th className="text-center text-nowrap">CASH</th>
                          <th className="text-center text-nowrap">UPI</th>
                          <th className="text-center text-nowrap">BANK</th>
                          <th className="text-center text-nowrap">CARD</th>
                          <th className="text-center text-nowrap">OLD GOLD</th>
                          <th className="text-center text-nowrap">OLD SILVER</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.name}</td>
                            <td
                              onClick={() => handleInvoiceClick(row)}
                              style={{
                                cursor: "pointer",
                              }}
                            >
                              {row.invoice_no}
                            </td>
                            <td>
                              {row.invoice_date}
                            </td>
                            <td className="text-end">
                              {formatCurrency(row.invoice_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.taxable_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.sgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.cgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.igst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.total_tax)}
                            </td>
                            <td className="text-end">
                              {formatWeight(row.gold_gm)}
                            </td>
                            <td className="text-end">
                              {formatWeight(row.silver_gm)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.cash)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.upi)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.bank)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(row.card)}
                            </td>
                            <td className="text-end">
                              {formatOldMetalPayment(
                                row.old_gold_amt,
                                row.old_gold_gm,
                              )}
                            </td>
                            <td className="text-end">
                              {formatOldMetalPayment(
                                row.old_silver_amt,
                                row.old_silver_gm,
                              )}
                            </td>
                          </tr>
                        ))}
                        {/* Summary Row */}
                        {summaryData && (
                          <tr className="ledger-report-summary">
                            <td colSpan="3"></td>
                            <td className="text-end">
                              {formatCurrency(summaryData.invoice_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.taxable_value)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.sgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.cgst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.igst)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.total_tax)}
                            </td>
                            <td className="text-end">
                              {formatWeight(summaryData.gold_gm)}
                            </td>
                            <td className="text-end">
                              {formatWeight(summaryData.silver_gm)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.cash)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.upi)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.bank)}
                            </td>
                            <td className="text-end">
                              {formatCurrencyDecimal(summaryData.card)}
                            </td>
                            <td className="text-end">
                              {formatOldMetalPayment(
                                summaryData.old_gold_amt,
                                summaryData.old_gold_gm,
                              )}
                            </td>
                            <td className="text-end">
                              {formatOldMetalPayment(
                                summaryData.old_silver_amt,
                                summaryData.old_silver_gm,
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {entireReport && totalCount > 0 && (
                  <Pagination
                    totalItems={totalCount}
                    hasMore={hasMorePages}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    itemsPerPageOptions={[10, 20, 30, 50, 100]}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Print Loading Modal */}
      <Modal
        show={printLoading}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="mb-2">Preparing Sales Ledger PDF</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
            This may take a few minutes. Thank you for your patience and understanding.
          </p>
        </Modal.Body>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
          }
          setPdfBlobUrl(null);
        }}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sales Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", padding: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              title="Sales Ledger PDF Preview"
              src={pdfBlobUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading PDF...</span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Edit Sale Modal — full edit form instead of navigating away */}
      <Modal
        show={showEditSaleModal}
        onHide={() => closeEditSaleModal()}
        size="xl"
        centered
        backdrop="static"
        scrollable
        dialogClassName="edit-sale-from-ledger-modal"
      >
        <Modal.Body className="p-0">
          {showEditSaleModal && editSaleId != null && (
            <ViewSalesInvoice
              key={`${editSaleId}-${String(editSaleIsQuote)}`}
              saleId={editSaleId}
              selectedItemIds={[editSaleId]}
              selectedInvoices={editSaleInvoices}
              onClose={closeEditSaleModal}
              masterGoldRate={masterGoldRate}
              masterSilverRate={masterSilverRate}
              showLiveRate={showLiveRate}
              isQuote={editSaleIsQuote}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default SalesLedger;
